import { MinsterNumberPickerComponent } from "../ui-components/minster-number-picker/minster-number-picker.component";
import { MinsterShowHideComponent } from "../ui-components/minster-show-hide/minster-show-hide.component";
import { MinsterCheckboxComponent } from "../ui-components/minster-checkbox/minster-checkbox.component";
import { MinsterSelectComponent } from "../ui-components/minster-select/minster-select.component";
import { ControlContainer, FormsModule, NgForm, NgModelGroup, Validators } from '@angular/forms';
import { MinsterInputComponent } from "../ui-components/minster-input/minster-input.component";
import { MinsterCardComponent } from "../ui-components/minster-card/minster-card.component";
import { ScreenBaseComponent } from '../screen-base/screen-base.component';
import { InputType, ScreenType } from '../../models/enums.models';
import { ClaimsService } from '../../services/claims.service';
import { StateService } from '../../services/state.service';
import { Claimant, Injury } from '../../models/claim.model';
import { MatButtonModule } from '@angular/material/button';
import { Component, ViewChild } from '@angular/core';
import { LookUp } from '../../models/look-up.model';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';

@Component({
    selector: 'app-employment',
    standalone: true,
    imports: [MinsterCardComponent, MinsterInputComponent, FormsModule, MinsterSelectComponent, MatButtonModule,
        MinsterCheckboxComponent, MinsterShowHideComponent, MinsterNumberPickerComponent],
    templateUrl: './employment.component.html',
    styleUrl: './employment.component.css',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})

export class EmploymentComponent extends ScreenBaseComponent {
    @ViewChild(NgModelGroup) _formGroup: NgModelGroup | undefined;

    _claimant: Claimant;
    _injury: Injury;
    _inputTypes = InputType;
    _occupationStatuses: LookUp[];
    _noNi: LookUp[];
    _readonly: boolean = false;

    constructor(stateService: StateService, toastr: ToastrService, router: Router, claimService: ClaimsService) {
        super(router, ScreenType.Employment, toastr, stateService, claimService);

        this._readonly = stateService.formIsSubmitted;
        this._claimant = stateService.claim!.claimant!;
        this._injury = stateService.claim?.injury!;
        this._occupationStatuses = stateService.lookUps?.lookUpDictionary['OccupationStatus']!;
        this._noNi = stateService.lookUps?.lookUpDictionary['NationalInsuranceReason']!;
    }

    override isScreenValid(): boolean {
        return this.stateService.getFormValidationErrorsExcludeRequired(this._formGroup!.control) == 0;
    }

    override onNext(): void {
         if(this._readonly){
            super.onNext();
            return;
        }
        let hasErrors = false;
        this.updateValidators();

        if (this._formGroup && this._formGroup.invalid) {
            Object.keys(this._formGroup.control.controls).forEach((key) => {
                const control = this._formGroup!.control.get(key);
                if (control) {
                    const errors = control.errors;

                    if (errors) {
                        const hasOnlyRequiredError = Object.keys(errors).length === 1 && errors['required'];
                        if (this.stateService.formIsSubmitted || !hasOnlyRequiredError) {
                            hasErrors = true;
                        }
                    }

                    control.markAsTouched();
                    control.markAsDirty();
                    control.updateValueAndValidity();
                }
            });

            if (hasErrors) {
                this.toastr.warning('Please fix the form errors before proceeding.');
                this.stateService.enableSaving = false;
                return;
            }
        }

        if (this.stateService.formIsSubmitted && this._formGroup!.invalid) {
            this.toastr.warning('Please fix the required fields before proceeding.');
            this.stateService.enableSaving = false;
            return;
        }
        
        this.stateService.enableSaving = this.hasStateChanged();

        super.onNext();
    }

    onNoNiReasonChanged(reasonId: number | null): void {
        if (reasonId === 1 || reasonId === 2) {
            this._claimant.nationalInsuranceNumber = '';
        }

        this.updateValidators();
    }

    onEmploymentStatusChanges(occupationStatusId: number | null): void {
        if (occupationStatusId === 3 ||
            occupationStatusId === 4 ||
            occupationStatusId === 5 ||
            occupationStatusId === 6 ||
            occupationStatusId === 7 ||
            occupationStatusId === 8 ||
            occupationStatusId === 9) {

            this._claimant.occupation = this.stateService.getLooUpValue('OccupationStatus', occupationStatusId);
        } else {
            this._claimant.occupation = '';
        }

        this.updateValidators();
    }

    get isNiDisabled(): boolean {
        return this._claimant.noNationalInsuranceNumberReasonId === 1 || this._claimant.noNationalInsuranceNumberReasonId === 2;
    }

    get isNoNiDisabled(): boolean {
        const niControl = this._formGroup?.control.get(this._fcn.Employment.Ni);
        const niFieldValid = niControl && niControl.valid && niControl.value != null && niControl.value !== '';

        return niFieldValid || false;
    }

    get isEmployed(): boolean {
        return this._claimant.occupationStatusId == 1 || this._claimant.occupationStatusId == 2;
    }

    get isIndependentMeans(): boolean {
        return this._claimant.occupationStatusId == 8;
    }

    get isOccupationVisible(): boolean {
        return (this._claimant.occupationStatusId == 1 ||
            this._claimant.occupationStatusId == 2 ||
            this._claimant.occupationStatusId == 6 ||
            this._claimant.occupationStatusId == 7 ||
            this._claimant.occupationStatusId == 8);
    }

    get niRequired(): boolean {
        return (
            this._claimant.nationalInsuranceNumber == null ||
            this._claimant.nationalInsuranceNumber === '' ||
            this._claimant.noNationalInsuranceNumberReasonId == null ||
            this._claimant.nationalInsuranceNumber !== 'N/A'
        );
    }

    updateValidators(): void {
        const controls = [
            { formName: this._fcn.Employment.Occupation, makeRequired: this.isEmployed, validators: [Validators.required, Validators.minLength(2), Validators.maxLength(100)], resetValue: '' },
            { formName: this._fcn.Employment.TimeOffWork, makeRequired: this.isEmployed, validators: [Validators.required], resetValue: null },
            { formName: this._fcn.Employment.StillOfWork, makeRequired: this.isEmployed && this._injury.hasTakenTimeOffWork, validators: [Validators.required], resetValue: 0 },
            { formName: this._fcn.Employment.StillOngoing, makeRequired: this.isEmployed && this._injury.hasTakenTimeOffWork, validators: [Validators.required], resetValue: null }
        ];

        controls.forEach(({ formName, makeRequired, validators, resetValue }) => {
            const control = this._formGroup?.control.get(formName);

            if (control) {
                if (makeRequired) {
                    if (!control.hasValidator(validators[0])) {
                        control.setValidators(validators);
                    }
                }
                else {
                    if (control.validator) {
                        control.clearValidators();
                        control.setValue(resetValue);
                    }
                }

                control.updateValueAndValidity();
            }
        });
    }
}
